import type { Seals } from "gql/api";
import type { EquipmentTableColumn } from "@/composables/equipment/equipmentTypes";

export const sealTableColumns: EquipmentTableColumn<Seals>[] = [
  {
    editable: true,
    field: "include_koc_timestamp",
    header: "Include KOC",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "only_koc_timestamp",
    header: "KOC Only",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "include_ecopetrol_timestamp",
    header: "Include Ecopetrol",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "only_ecopetrol_timestamp",
    header: "Ecopetrol Only",
    sortable: true,
    isCheckbox: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "manufacturer",
    header: "Manufacturer",
    sortable: true,
    searchable: true
  },
  {
    editable: true,
    field: "part_number",
    header: "Part Number",
    sortable: true,
    adminOnly: true,
    searchable: true
  },
  {
    editable: true,
    field: "configuration",
    header: "Configuration",
    sortable: true,
    searchable: true
  },
  {
    editable: true,
    field: "description",
    header: "Description",
    sortable: true,
    adminOnly: true
  },
  {
    editable: true,
    field: "bearing_type",
    header: "Bearing Type",
    sortable: true
  },
  {
    editable: true,
    field: "housing_material",
    header: "Housing Material",
    adminOnly: true,
    sortable: true
  },
  {
    editable: true,
    field: "outer_diameter_inches",
    header: "Outer Diameter (in)",
    headerShort: "OD (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "inner_diameter_inches",
    header: "Inner Diameter (in)",
    headerShort: "ID (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "thrust_collar_outer_diameter_inches",
    header: "Thrust Collar Outer Diameter (in)",
    headerShort: "Thrust Collar OD (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "thrust_collar_inner_diameter_inches",
    header: "Thrust Collar Inner Diameter (in)",
    headerShort: "Thrust Collar ID (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "neck_outer_diameter_inches",
    header: "Neck Outer Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "neck_inner_diameter_inches",
    header: "Neck Inner Diameter (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6,
    adminOnly: true
  },
  {
    editable: true,
    field: "length_inches",
    header: "Length (in)",
    isNumber: true,
    sortable: true,
    searchable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "weight_pounds",
    header: "Weight (lb)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "max_temperature_fahrenheit",
    header: "Max Temp (F)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    searchable: true
  },
  {
    editable: true,
    field: "standard_shaft_limit_hp",
    header: "Standard Shaft Limit (HP)",
    isNumber: true,
    adminOnly: true,
    sortable: true
  },
  {
    editable: true,
    field: "high_strength_shaft_limit_hp",
    header: "High Strength Shaft Limit (HP)",
    isNumber: true,
    adminOnly: true,
    sortable: true
  },
  {
    editable: true,
    field: "standard_bearing_rating_pounds",
    header: "Standard Bearing Rating (lb)",
    isNumber: true,
    sortable: true
  },
  {
    editable: true,
    field: "high_strength_bearing_rating_pounds",
    header: "High Strength Bearing Rating (lb)",
    isNumber: true,
    adminOnly: true,
    sortable: true
  },
  {
    editable: true,
    field: "total_seal_volume_gallons",
    header: "Total Seal Volume (gal)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "bag_capacity_gallons",
    header: "Individual Bag Capacity (gal)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "volume_for_bag_expansion_upper_tandem_gallons",
    header: "Volume for Expansion Upper Tandem (gal)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "volume_for_bag_expansion_lower_tandem_gallons",
    header: "Volume for Expansion Lower Tandem (gal)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "upper_chamber_pull_volume_gallons",
    header: "Upper Chamber Pull Volume (gal)",
    isNumber: true,
    adminOnly: true,
    sortable: true
  },
  {
    editable: true,
    field: "bag_chambers",
    header: "Bag Chambers",
    isNumber: true,
    adminOnly: true,
    sortable: true
  },
  {
    editable: true,
    field: "string_load_pounds",
    header: "String Load (lb)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "load_cb_pounds",
    header: "Load CB (lb)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "load_c1_pounds",
    header: "Load C1 (lb)",
    isNumber: true,
    adminOnly: true,
    sortable: true,
    decimalPlaces: 6
  },
  {
    editable: true,
    field: "load_test_temperature_fahrenheit",
    header: "Load Test Temperature (F)",
    isNumber: true,
    adminOnly: true,
    sortable: true
  }
];
